import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  NotFetchedYet
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'

type PictureVariant = 'mini' | 'small' | 'normal' | 'large'

@customElement('csc-quotation-product-preview')
export class CSCQuotationProductPreview extends LitElement {
  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @property({ type: Number, reflect: true, attribute: 'line-id' })
  lineId!: number

  @property({ type: String })
  variant: 'large' | 'small' = 'small'

  @property({ type: Boolean, attribute: 'show-stock' })
  showStock: boolean = false

  @property({ type: Boolean, attribute: 'show-comments' })
  showComments: boolean = false

  private get product() {
    if (!isQuotationDTO(this.quotation)) {
      throw new Error('Quotation is not available.')
    }

    return (this.quotation.quotationLines.find(line => line.id === this.lineId))?.product
  }

  private get name(): string {
    return this.product?.name || ''
  }

  private get code(): string {
    return this.product?.code || ''
  }

  private get price(): string {
    if (!(this.quotation instanceof QuotationDTO) || !this.quotation.showPrice) {
      return ''
    }

    return this.quotation.quotationLines.find(line => line.id === this.lineId)?.priceUom.price || ''
  }

  private picture(variant: PictureVariant = 'normal'): string {
    return this.product?.picture?.[variant] || ''
  }

  constructor() {
    super()

    if (this.variant === 'large') {
      throw new Error('The "large" variant is not implemented yet.')
    }

    if (this.showStock) {
      throw new Error('The "showStock" feature is not implemented yet.')
    }

    if (this.showComments) {
      throw new Error('The "showComments" feature is not implemented yet.')
    }
  }

  override render() {
    return html`
      <csc-product-preview
        product-name="${this.name}"
        product-id="${this.code}"
        price="${this.price}"
        size="${this.variant}"
        image-source="${this.picture()}"
      ></csc-product-preview>
    `
  }
}
